import { motion } from "framer-motion";
import type { DragEvent } from "react";
import { useCallback, useRef, useState } from "react";
import { FiUploadCloud } from "react-icons/fi";
import SafeIcon from "./SafeIcon";
import type { ConverterTheme } from "./types";

interface DropzoneCopy {
  ariaLabel: string;
  draggingTitle: string;
  idleTitle: string;
  browseLabel: string;
  privacyLine: string;
}

interface DropzoneProps {
  onFilesAdded: (files: File[]) => void;
  acceptedExt: string[];
  formatPills: string[];
  maxBytes: number;
  formatBytes: (bytes: number) => string;
  isSupportedFile: (file: File) => boolean;
  isOversizedFile: (file: File) => boolean;
  copy: DropzoneCopy;
  theme: ConverterTheme;
}

const Dropzone = ({
  onFilesAdded,
  acceptedExt,
  formatPills,
  maxBytes,
  formatBytes,
  isSupportedFile,
  isOversizedFile,
  copy,
  theme,
}: DropzoneProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [rejection, setRejection] = useState<string | null>(null);

  const handleFiles = useCallback(
    (fileList: FileList | File[] | null) => {
      if (!fileList) return;
      const files = Array.from(fileList);
      const accepted = files.filter(isSupportedFile);
      const oversized = files.filter(isOversizedFile).length;
      const unsupported = files.length - accepted.length - oversized;

      if (oversized || unsupported) {
        const parts = [
          oversized ? `${oversized} over ${formatBytes(maxBytes)}` : null,
          unsupported ? `${unsupported} unsupported` : null,
        ].filter(Boolean);
        setRejection(
          `Skipped ${parts.join(" and ")} file${oversized + unsupported > 1 ? "s" : ""}.`,
        );
      } else {
        setRejection(null);
      }

      if (accepted.length) onFilesAdded(accepted);
    },
    [formatBytes, isOversizedFile, isSupportedFile, maxBytes, onFilesAdded],
  );

  const handleDrop = useCallback(
    (e: DragEvent<HTMLButtonElement>) => {
      e.preventDefault();
      setIsDragging(false);
      if (e.dataTransfer.files) handleFiles(e.dataTransfer.files);
    },
    [handleFiles],
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.15 }}
      className="mx-auto mb-10 w-full max-w-3xl"
    >
      <input
        type="file"
        multiple
        accept={acceptedExt.join(",")}
        className="hidden"
        ref={fileInputRef}
        onChange={(e) => {
          handleFiles(e.target.files);
          e.target.value = "";
        }}
      />

      <button
        type="button"
        aria-label={copy.ariaLabel}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            fileInputRef.current?.click();
          }
        }}
        className={`glass group w-full cursor-pointer select-none rounded-3xl border-2 border-dashed p-8 text-center outline-none transition-[border-color,background-color,box-shadow,transform] touch-manipulation focus-visible:ring-2 ${theme.focusRing} sm:p-10 md:rounded-[2rem] md:p-14 ${
          isDragging
            ? theme.dropActive
            : `border-slate-300 hover:shadow-lg dark:border-slate-700 ${theme.dropHover}`
        }`}
      >
        <motion.div
          animate={{ scale: isDragging ? 1.2 : 1 }}
          transition={{ type: "spring", stiffness: 300 }}
          className={`${theme.uploadIconBox} mx-auto mb-5 flex h-20 w-20 items-center justify-center rounded-3xl shadow-inner`}
        >
          <SafeIcon
            icon={FiUploadCloud}
            className={`text-4xl ${theme.uploadIcon}`}
          />
        </motion.div>

        <h2 className="mb-2 text-2xl font-black text-slate-800 md:text-3xl dark:text-white">
          {isDragging ? copy.draggingTitle : copy.idleTitle}
        </h2>
        <p className="mb-6 text-sm text-slate-500 dark:text-slate-400">
          or{" "}
          <span
            className={`${theme.accentText} font-bold underline underline-offset-2`}
          >
            {copy.browseLabel}
          </span>{" "}
          your files
        </p>

        <div className="flex flex-wrap justify-center gap-2">
          {formatPills.map((format) => (
            <span
              key={format}
              className="rounded-full border border-slate-200 bg-white px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400"
            >
              {format}
            </span>
          ))}
        </div>

        <p className="mt-4 text-[11px] text-slate-400">
          {copy.privacyLine} · Files up to {formatBytes(maxBytes)} stay on your
          device
        </p>
        {rejection && (
          <p
            className="mt-3 text-xs font-bold text-amber-600 dark:text-amber-300"
            role="status"
          >
            {rejection}
          </p>
        )}
      </button>
    </motion.div>
  );
};

export default Dropzone;
